import React, { useRef, useEffect, useCallback } from 'react';
import Konva from 'konva';

export const FIELD_WIDTH_FT = 120;   // sideline-to-sideline
export const FIELD_LENGTH_FT = 150;  // endline-to-endline (the long axis, split by centerline)

const ZOOM_MIN = 0.3;
const ZOOM_MAX = 8;
const ZOOM_STEP = 1.15;

export type FieldOrientation = 'horizontal' | 'vertical';

export interface FieldTransform {
  basePxPerFoot: number;
  fieldOriginX: number;
  fieldOriginY: number;
  orientation: FieldOrientation;
  /** Feet in the horizontal (left↔right) canvas direction. */
  canvasHorizFt: number;
  /** Feet in the vertical (top↕bottom) canvas direction. */
  canvasVertFt: number;
}

interface FieldCanvasProps {
  containerWidth: number;
  containerHeight: number;
  orientation: FieldOrientation;
  onReady?: (stage: Konva.Stage, transform: FieldTransform) => void;
}

function computeTransform(
  containerWidth: number,
  containerHeight: number,
  orientation: FieldOrientation,
): FieldTransform {
  const padding = 40;
  // In horizontal mode the 150ft length runs left-right; in vertical it runs top-bottom.
  const horizFt = orientation === 'horizontal' ? FIELD_LENGTH_FT : FIELD_WIDTH_FT;
  const vertFt  = orientation === 'horizontal' ? FIELD_WIDTH_FT  : FIELD_LENGTH_FT;

  const scaleX = (containerWidth  - padding * 2) / horizFt;
  const scaleY = (containerHeight - padding * 2) / vertFt;
  const basePxPerFoot = Math.min(scaleX, scaleY);

  const fieldWidthPx  = horizFt * basePxPerFoot;
  const fieldHeightPx = vertFt  * basePxPerFoot;
  const fieldOriginX  = (containerWidth  - fieldWidthPx)  / 2;
  const fieldOriginY  = (containerHeight - fieldHeightPx) / 2;

  return {
    basePxPerFoot,
    fieldOriginX,
    fieldOriginY,
    orientation,
    canvasHorizFt: horizFt,
    canvasVertFt:  vertFt,
  };
}

function drawField(layer: Konva.Layer, t: FieldTransform) {
  layer.destroyChildren();

  const { basePxPerFoot, fieldOriginX: ox, fieldOriginY: oy,
          canvasHorizFt, canvasVertFt, orientation } = t;
  const fw = canvasHorizFt * basePxPerFoot;
  const fh = canvasVertFt  * basePxPerFoot;

  // Field surface
  layer.add(new Konva.Rect({
    x: ox, y: oy, width: fw, height: fh,
    fill: '#2d5a1b', stroke: '#ffffff', strokeWidth: 2,
    listening: false,
  }));

  // Centerlines — bisect both axes so the field center is easy to spot
  const midLength = (FIELD_LENGTH_FT / 2) * basePxPerFoot;
  const midWidth  = (FIELD_WIDTH_FT  / 2) * basePxPerFoot;

  const lengthLinePts = orientation === 'horizontal'
    ? [ox + midLength, oy, ox + midLength, oy + fh]  // vertical line (bisects length axis)
    : [ox, oy + midLength, ox + fw, oy + midLength]; // horizontal line

  const widthLinePts = orientation === 'horizontal'
    ? [ox, oy + midWidth, ox + fw, oy + midWidth]    // horizontal line (bisects width axis)
    : [ox + midWidth, oy, ox + midWidth, oy + fh];   // vertical line

  const lineStyle = { stroke: '#ffffff', strokeWidth: 1, dash: [10, 6], opacity: 0.75, listening: false };
  layer.add(new Konva.Line({ points: lengthLinePts, ...lineStyle }));
  layer.add(new Konva.Line({ points: widthLinePts,  ...lineStyle }));

  layer.batchDraw();
}

export const FieldCanvas: React.FC<FieldCanvasProps> = ({
  containerWidth,
  containerHeight,
  orientation,
  onReady,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const stageRef     = useRef<Konva.Stage | null>(null);
  const transformRef = useRef<FieldTransform | null>(null);

  const fitToScreen = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    stage.scale({ x: 1, y: 1 });
    stage.position({ x: 0, y: 0 });
    stage.batchDraw();
  }, []);

  useEffect(() => {
    (window as any).__fieldFitToScreen = fitToScreen;
    return () => { delete (window as any).__fieldFitToScreen; };
  }, [fitToScreen]);

  useEffect(() => {
    if (!containerRef.current) return;

    const transform = computeTransform(containerWidth, containerHeight, orientation);
    transformRef.current = transform;

    const stage = new Konva.Stage({
      container: containerRef.current,
      width: containerWidth,
      height: containerHeight,
    });
    stageRef.current = stage;

    const fieldLayer = new Konva.Layer();
    stage.add(fieldLayer);
    drawField(fieldLayer, transform);

    const contentLayer = new Konva.Layer();
    contentLayer.name('content');
    stage.add(contentLayer);

    onReady?.(stage, transform);

    // Zoom
    stage.on('wheel', (e) => {
      e.evt.preventDefault();
      const oldScale = stage.scaleX();
      const pointer  = stage.getPointerPosition()!;
      const mpTo = {
        x: (pointer.x - stage.x()) / oldScale,
        y: (pointer.y - stage.y()) / oldScale,
      };
      const dir      = e.evt.deltaY < 0 ? 1 : -1;
      const newScale = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN,
        oldScale * (dir > 0 ? ZOOM_STEP : 1 / ZOOM_STEP)));
      stage.scale({ x: newScale, y: newScale });
      stage.position({
        x: pointer.x - mpTo.x * newScale,
        y: pointer.y - mpTo.y * newScale,
      });
      stage.batchDraw();
    });

    // Pan
    let isPanning = false;
    let lastPos = { x: 0, y: 0 };
    let spaceHeld = false;

    const onKeyDown = (e: KeyboardEvent) => { if (e.code === 'Space') { spaceHeld = true; e.preventDefault(); } };
    const onKeyUp   = (e: KeyboardEvent) => { if (e.code === 'Space') spaceHeld = false; };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup',   onKeyUp);

    stage.on('mousedown', (e) => {
      if (e.evt.button === 1 || spaceHeld) {
        isPanning = true;
        lastPos = stage.getPointerPosition()!;
        stage.container().style.cursor = 'grabbing';
      }
    });
    stage.on('mousemove', () => {
      if (!isPanning) return;
      const pos = stage.getPointerPosition()!;
      stage.position({ x: stage.x() + (pos.x - lastPos.x), y: stage.y() + (pos.y - lastPos.y) });
      lastPos = pos;
      stage.batchDraw();
    });
    stage.on('mouseup mouseleave', () => {
      if (isPanning) { isPanning = false; stage.container().style.cursor = 'default'; }
    });

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup',   onKeyUp);
      stage.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerWidth, containerHeight, orientation]);

  return (
    <div
      ref={containerRef}
      style={{ width: containerWidth, height: containerHeight, overflow: 'hidden' }}
    />
  );
};
